import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Coins, LayoutGrid, LogOut, Shield, Sparkles, User as UserIcon, Wand2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { LanguageToggle } from '@/components/LanguageToggle';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const navItems = [
    { to: '/studyo', label: t('nav.studio'), icon: Wand2, testId: 'nav-studio' },
    { to: '/galeri', label: t('nav.gallery'), icon: LayoutGrid, testId: 'nav-gallery' },
    { to: '/hesabim', label: t('nav.account'), icon: UserIcon, testId: 'nav-account' },
  ];

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))]">
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--border-soft))] bg-[hsl(var(--bg-2))]">
        <div className="mx-auto flex h-14 max-w-[1280px] items-center gap-3 px-4 sm:px-6 lg:px-8">
          <Link to="/studyo" className="shrink-0">
            <Logo />
          </Link>

          <nav className="ml-4 hidden items-center gap-1 md:flex">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                className={({ isActive }) =>
                  [
                    'flex items-center gap-2 rounded-[10px] px-3 py-2 text-sm font-medium transition-colors duration-150',
                    isActive
                      ? 'bg-[hsl(var(--surface-2))] text-white'
                      : 'text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--surface))] hover:text-white',
                  ].join(' ')
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
            {user?.role === 'admin' && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  [
                    'flex items-center gap-2 rounded-[10px] px-3 py-2 text-sm font-medium transition-colors duration-150',
                    isActive
                      ? 'bg-[hsl(var(--surface-2))] text-white'
                      : 'text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--surface))] hover:text-white',
                  ].join(' ')
                }
              >
                <Shield className="h-4 w-4" />
                {t('nav.admin')}
              </NavLink>
            )}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <LanguageToggle />

            <Link
              to="/hesabim"
              data-testid="credits-balance-badge"
              className="flex items-center gap-1.5 rounded-full border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] px-3 py-1.5 text-xs font-semibold text-[hsl(var(--text))] transition-colors duration-150 hover:border-[hsl(var(--brand-red))]"
            >
              <Coins className="h-3.5 w-3.5 text-[hsl(var(--brand-red))]" />
              <span className="font-mono-alt">{user?.credits ?? 0}</span>
              <span className="hidden text-[hsl(var(--text-muted))] sm:inline">{t('nav.creditsShort')}</span>
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="profile-menu-button"
                  className="h-9 w-9 overflow-hidden rounded-full border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] p-0 hover:bg-[hsl(var(--surface-2))]"
                >
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user?.name || user?.email || ''}
                      referrerPolicy="no-referrer"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-xs font-semibold uppercase">
                      {(user?.name || user?.email || '?').slice(0, 2)}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]">
                <DropdownMenuLabel className="text-xs">
                  <div className="font-semibold text-white">{user?.name}</div>
                  <div className="truncate text-[hsl(var(--text-muted))]">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/studyo')} data-testid="menu-studio">
                  <Wand2 className="mr-2 h-4 w-4" /> {t('nav.studio')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/galeri')} data-testid="menu-gallery">
                  <LayoutGrid className="mr-2 h-4 w-4" /> {t('nav.gallery')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/hesabim')} data-testid="menu-account">
                  <Sparkles className="mr-2 h-4 w-4" /> {t('nav.accountCredits')}
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                    <Shield className="mr-2 h-4 w-4" /> {t('nav.adminPanel')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                  className="text-[hsl(var(--brand-red))] focus:text-[hsl(var(--brand-red))]"
                >
                  <LogOut className="mr-2 h-4 w-4" /> {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <nav className="flex items-center gap-1 overflow-x-auto border-t border-[hsl(var(--border-soft))] px-4 py-2 md:hidden">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                [
                  'flex shrink-0 items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-xs font-medium transition-colors duration-150',
                  isActive ? 'bg-[hsl(var(--surface-2))] text-white' : 'text-[hsl(var(--text-muted))]',
                ].join(' ')
              }
            >
              <n.icon className="h-3.5 w-3.5" />
              {n.label}
            </NavLink>
          ))}
          {user?.role === 'admin' && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                [
                  'flex shrink-0 items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-xs font-medium',
                  isActive ? 'bg-[hsl(var(--surface-2))] text-white' : 'text-[hsl(var(--text-muted))]',
                ].join(' ')
              }
            >
              <Shield className="h-3.5 w-3.5" /> {t('nav.admin')}
            </NavLink>
          )}
        </nav>
      </header>

      <main className="mx-auto max-w-[1280px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</main>
    </div>
  );
};
